import React from 'react';
import { Box, Typography } from '@mui/material';
import { Apple as AppleIcon } from '@mui/icons-material';

/**
 * App-Store-Badges („Laden im App Store“ / „Jetzt bei Google Play“).
 *
 * ⚠️ Wichtig für den Produktivbetrieb: Apple und Google verlangen laut ihren
 * Marken-Richtlinien die **unveränderten Original-Grafiken** aus
 *   • Apple Marketing Resources  (developer.apple.com → App Store Marketing)
 *   • Google Play Badge Generator (play.google.com/intl/de/badges/)
 * Die hier gezeichneten Badges sind maßstabsgetreue Nachbauten, damit die Seite
 * ohne externe Assets funktioniert. Sobald die offiziellen SVG/PNG-Dateien unter
 * `public/assets/` liegen, genügt es, den Inhalt der beiden Badge-Komponenten
 * durch ein <Box component="img" src="/assets/…" /> zu ersetzen — Größe,
 * Abstände und Links bleiben unverändert.
 *
 * Die Store-URLs werden in Firestore unter `appSettings/general`
 * (`appStoreUrl`, `playStoreUrl`) gepflegt; ohne hinterlegte URL zeigt das Badge
 * auf `/mobile-app`, das die Plattformweiche und den QR-Code übernimmt.
 */

const badgeSx = (height) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: `${height * 0.16}px`,
  height,
  px: `${height * 0.28}px`,
  borderRadius: `${height * 0.17}px`,
  bgcolor: '#000',
  border: '1px solid rgba(255,255,255,0.45)',
  color: '#fff',
  textDecoration: 'none',
  flexShrink: 0,
  transition: 'border-color .2s ease, transform .2s ease',
  '&:hover': {
    borderColor: '#fff',
    transform: 'translateY(-2px)',
  },
});

const captionSx = (height) => ({
  display: 'block',
  fontSize: height * 0.18,
  lineHeight: 1.1,
  letterSpacing: 0.4,
  color: '#fff',
});

const wordmarkSx = (height) => ({
  display: 'block',
  fontSize: height * 0.34,
  lineHeight: 1.15,
  fontWeight: 600,
  letterSpacing: -0.2,
  color: '#fff',
});

/** Google-Play-Dreieck in den vier Markenfarben. */
const PlayGlyph = ({ size }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: size, height: size, flexShrink: 0 }}
    aria-hidden="true"
  >
    <path d="M3.6 1.8c-.3.3-.5.8-.5 1.4v17.6c0 .6.2 1.1.5 1.4l.1.1 9.9-9.9v-.2L3.6 1.8z" fill="#00A0FF" />
    <path d="M16.8 15.7l-3.3-3.3v-.2l3.3-3.3.1.1 3.9 2.2c1.1.6 1.1 1.7 0 2.3l-3.9 2.2z" fill="#FFBC00" />
    <path d="M16.9 15.6l-3.4-3.4-9.9 9.9c.4.4 1 .4 1.7.1l11.6-6.6z" fill="#FF3A44" />
    <path d="M16.9 8.8L5.3 2.2c-.7-.4-1.3-.3-1.7.1l9.9 9.9 3.4-3.4z" fill="#00C853" />
  </Box>
);

export const AppStoreBadge = ({ href = '/mobile-app', height = 48 }) => (
  <Box
    component="a"
    href={href}
    target={href.startsWith('http') ? '_blank' : undefined}
    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
    aria-label="Laden im App Store"
    sx={badgeSx(height)}
  >
    <AppleIcon sx={{ fontSize: height * 0.58, mb: `${height * 0.05}px` }} />
    <Box>
      <Typography component="span" sx={captionSx(height)}>
        Laden im
      </Typography>
      <Typography component="span" sx={wordmarkSx(height)}>
        App Store
      </Typography>
    </Box>
  </Box>
);

export const GooglePlayBadge = ({ href = '/mobile-app', height = 48 }) => (
  <Box
    component="a"
    href={href}
    target={href.startsWith('http') ? '_blank' : undefined}
    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
    aria-label="Jetzt bei Google Play"
    sx={badgeSx(height)}
  >
    <PlayGlyph size={height * 0.5} />
    <Box>
      <Typography component="span" sx={{ ...captionSx(height), textTransform: 'uppercase' }}>
        Jetzt bei
      </Typography>
      <Typography component="span" sx={wordmarkSx(height)}>
        Google Play
      </Typography>
    </Box>
  </Box>
);

/** Beide Badges nebeneinander, umbruchsicher. */
const StoreBadges = ({ appStoreUrl, playStoreUrl, height = 48, justify = 'flex-start' }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: justify }}>
    <AppStoreBadge href={appStoreUrl || '/mobile-app'} height={height} />
    <GooglePlayBadge href={playStoreUrl || '/mobile-app'} height={height} />
  </Box>
);

export default StoreBadges;
